/**
 * appointmentResources.service.ts — Admin > Appointment Resources: the
 * account's schedulable resources (providers, rooms, equipment…), edited
 * through /v5/resources as the corporate dashboard does.
 *
 *   - The list endpoint returns live OR deleted resources, never both, so the
 *     admin list asks for each and merges them (as corp does).
 *   - A delete makes the resource unavailable; restore makes it available.
 *   - facility_ids empty = available at every facility. An update replaces
 *     the set whenever it is sent.
 * Writes need Admin Access. (The scheduler keeps reading resources through
 * appointments.service.)
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export type ResourceType = 'doctors' | 'coordinators' | 'nurses' | 'rooms' | 'equipment' | 'other';

/** Corp's types and generic labels; staff types take the account's role titles on screen. */
export const RESOURCE_TYPES: { value: ResourceType; label: string }[] = [
  { value: 'doctors', label: 'Medical Provider' },
  { value: 'coordinators', label: 'Coordinator' },
  { value: 'nurses', label: 'Nurse' },
  { value: 'rooms', label: 'Room' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'other', label: 'Other' },
];

/** The types tied to a staff member (type_table_id is their id). */
export const USER_LINKED_TYPES: ResourceType[] = ['doctors', 'coordinators', 'nurses'];

export interface AdminResource {
  resourceID: number;
  name: string;
  resourceType: ResourceType;
  description: string;
  /** The doctor / nurse / coordinator id for a staff resource; 0 otherwise. */
  typeTableId: number;
  /** The linked staff member's name, when the API sends one. */
  userName: string;
  facilityIds: number[];
  active: boolean;
}

export interface ResourcePayload {
  name: string;
  resource_type: ResourceType;
  description: string;
  type_table_id: number;
  facility_ids: number[];
}

const TYPES = new Set<string>(RESOURCE_TYPES.map((t) => t.value));

function personName(raw: unknown): string {
  if (!raw || typeof raw !== 'object') return '';
  const p = raw as Record<string, unknown>;
  return [p.first_name, p.last_name, p.suffix].map((v) => String(v ?? '').trim()).filter(Boolean).join(' ');
}

function normalizeResource(row: Record<string, unknown>, active: boolean): AdminResource {
  const type = String(row.resource_type ?? 'other');
  return {
    resourceID: Number(row.resourceID ?? 0),
    name: String(row.name ?? ''),
    resourceType: (TYPES.has(type) ? type : 'other') as ResourceType,
    description: String(row.description ?? ''),
    typeTableId: Number(row.type_table_id ?? 0) || 0,
    userName: personName(row.doctor ?? row.nurse ?? row.coordinator),
    facilityIds: Array.isArray(row.facility_ids) ? row.facility_ids.map(Number).filter((n) => n > 0) : [],
    active,
  };
}

/** Every resource, live and deleted. */
export async function listAdminResources(): Promise<AdminResource[]> {
  const [live, gone] = await Promise.all([
    apiClient.get<ApiResponse<Record<string, unknown>[]>>('/resources', { params: { active: true } }),
    apiClient.get<ApiResponse<Record<string, unknown>[]>>('/resources', { params: { active: false } }),
  ]);
  const rows = (r: { data: ApiResponse<Record<string, unknown>[]> }) => (Array.isArray(r.data?.data) ? r.data.data : []);
  return [...rows(live).map((x) => normalizeResource(x, true)), ...rows(gone).map((x) => normalizeResource(x, false))];
}

export function createResource(data: ResourcePayload): Promise<void> {
  return apiClient.post('/resources', data).then(() => undefined);
}

export function updateResource(id: number, data: ResourcePayload): Promise<void> {
  return apiClient.put(`/resources/${id}`, data).then(() => undefined);
}

export function deleteResource(id: number): Promise<void> {
  return apiClient.delete(`/resources/${id}`).then(() => undefined);
}

export function restoreResource(id: number): Promise<void> {
  return apiClient.post(`/resources/${id}/restore`).then(() => undefined);
}
